<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import parsePhoneNumber from 'libphonenumber-js';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useI18n } from 'vue-i18n';
import countries from 'shared/constants/countries.js';
import { getActiveCountryCode } from 'shared/components/PhoneInput/helper';

import Input from 'dashboard/components-next/input/Input.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showBorder: {
    type: Boolean,
    default: true,
  },
  /** ISO 3166-1 alpha-2 (e.g. EG) applied when model is empty (e.g. account country without a saved number). */
  countryHint: {
    type: String,
    default: '',
  },
});

const modelValue = defineModel({
  type: [String, Number],
  default: '',
});

const countriesByDialLengthDesc = [...countries].sort(
  (a, b) => b.dial_code.length - a.dial_code.length
);

const { t } = useI18n();

const showDropdown = ref(false);
const activeCountryCode = ref(getActiveCountryCode());
const rawPhoneInput = ref('');
/** After blur we may show validation; reset on focus so RTL/paste quirks don't stick. */
const phoneFieldBlurred = ref(false);
let syncingFromModel = false;

function normalizePhone(value) {
  if (value == null) return '';
  let s = String(value).trim();
  s = s.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '');
  s = s.replace(/[\u0660-\u0669]/g, ch => String(ch.charCodeAt(0) - 0x0660));
  s = s.replace(/[\u06F0-\u06F9]/g, ch => String(ch.charCodeAt(0) - 0x06f0));
  s = s.replace(/[\s\u00A0\u202F\uFEFF]+/g, '');
  if (!s) return '';
  const hasPlus = s.startsWith('+');
  const digits = s.replace(/\D/g, '');
  if (!digits) return '';
  return hasPlus ? `+${digits}` : digits;
}

function matchCountryByDialPrefix(compact) {
  if (!compact.startsWith('+')) return null;
  return countriesByDialLengthDesc.find(c => compact.startsWith(c.dial_code));
}

function syncCountryFromCompact(compact) {
  if (!compact || !compact.startsWith('+')) return;
  let parsed;
  try {
    parsed = parsePhoneNumber(
      compact,
      activeCountryCode.value || props.countryHint || undefined
    );
  } catch {
    parsed = undefined;
  }
  if (parsed?.country) {
    activeCountryCode.value = parsed.country;
    return;
  }
  const byDial = matchCountryByDialPrefix(compact);
  if (byDial) {
    activeCountryCode.value = byDial.id;
  }
}

function applyDefaultCountryFromHint() {
  if (!props.countryHint) return false;
  const c = countries.find(x => x.id === props.countryHint);
  if (!c) return false;
  activeCountryCode.value = c.id;
  return true;
}

function hydrateFromModel(newValue) {
  phoneFieldBlurred.value = false;
  const raw = newValue != null ? String(newValue).trim() : '';
  if (!raw) {
    rawPhoneInput.value = '';
    if (!applyDefaultCountryFromHint()) {
      activeCountryCode.value = getActiveCountryCode();
    }
    return;
  }
  let parsed;
  try {
    parsed = parsePhoneNumber(raw);
  } catch {
    parsed = undefined;
  }
  if (parsed) {
    activeCountryCode.value = parsed.country || activeCountryCode.value;
    rawPhoneInput.value = parsed.formatInternational();
    return;
  }
  rawPhoneInput.value = raw;
  syncCountryFromCompact(normalizePhone(raw));
}

function onPhoneFocus() {
  phoneFieldBlurred.value = false;
}

function onPhoneBlur() {
  phoneFieldBlurred.value = true;
}

function isPhoneValidE164(n) {
  if (!n) return true;
  if (isValidPhoneNumber(n)) return true;
  try {
    return parsePhoneNumber(n).isValid() === true;
  } catch {
    return false;
  }
}

const countryList = computed(() =>
  countries.map(country => ({
    value: country.id,
    label: `${country.name} ${country.dial_code}`,
    dialCode: country.dial_code,
    emoji: country.emoji,
    isSelected: String(activeCountryCode.value) === String(country.id),
    action: 'phoneNumberInput',
  }))
);

const activeCountry = computed(() =>
  activeCountryCode.value
    ? countries.find(c => c.id === activeCountryCode.value)
    : null
);

const phoneNumberError = computed(() => {
  if (!phoneFieldBlurred.value) return '';
  const n = normalizePhone(rawPhoneInput.value);
  if (!n) return '';
  return isPhoneValidE164(n) ? '' : t('PHONE_INPUT.ERROR');
});

const hasError = computed(() => Boolean(phoneNumberError.value));

const inputBorderClass = computed(() => {
  const errorClass =
    'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8';
  const focusClass =
    'has-[:focus]:outline-n-brand dark:has-[:focus]:outline-n-brand';

  if (!props.showBorder) {
    if (hasError.value) return errorClass;
    return `outline-transparent ${focusClass}`;
  }

  if (hasError.value) {
    return errorClass;
  }
  return `${focusClass} outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 disabled:outline-n-weak dark:disabled:outline-n-weak`;
});

const toggleCountryDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const closeCountryDropdown = () => {
  showDropdown.value = false;
};

const onSelectCountry = async ({ value, dialCode }) => {
  if (!value || !showDropdown.value) return;

  const previousCountry = activeCountryCode.value;
  const compact = normalizePhone(rawPhoneInput.value);
  showDropdown.value = false;

  let nationalDigits = '';
  try {
    const prev = parsePhoneNumber(compact, previousCountry || undefined);
    if (prev?.nationalNumber) {
      nationalDigits = prev.nationalNumber;
    }
  } catch {
    // ignore
  }
  if (!nationalDigits && compact.startsWith('+')) {
    nationalDigits = compact.replace(/^\+\d+/, '').replace(/\D/g, '');
  }

  activeCountryCode.value = value;

  rawPhoneInput.value = nationalDigits
    ? `${dialCode} ${nationalDigits}`.trim()
    : dialCode;

  await nextTick();
  const n = normalizePhone(rawPhoneInput.value);
  if (isPhoneValidE164(n)) {
    modelValue.value = parsePhoneNumber(n).format('E.164');
  }
};

watch(rawPhoneInput, async value => {
  if (syncingFromModel) return;
  const compact = normalizePhone(value);
  syncCountryFromCompact(compact);
  if (!compact) {
    if (modelValue.value !== '') {
      modelValue.value = '';
    }
    return;
  }
  if (isPhoneValidE164(compact)) {
    const e164 = parsePhoneNumber(compact).format('E.164');
    if (e164 !== modelValue.value) {
      modelValue.value = e164;
    }
  }
});

watch(
  () => modelValue.value,
  newValue => {
    syncingFromModel = true;
    hydrateFromModel(newValue);
    nextTick(() => {
      syncingFromModel = false;
    });
  },
  { immediate: true }
);

watch(
  () => props.countryHint,
  () => {
    const raw = modelValue.value != null ? String(modelValue.value).trim() : '';
    if (raw) return;
    phoneFieldBlurred.value = false;
    syncingFromModel = true;
    rawPhoneInput.value = '';
    if (!applyDefaultCountryFromHint()) {
      activeCountryCode.value = getActiveCountryCode();
    }
    nextTick(() => {
      syncingFromModel = false;
    });
  }
);

defineExpose({
  getPhoneParts() {
    const compact = normalizePhone(rawPhoneInput.value);
    if (!compact) {
      return { phone_country_code: null, phone_number: null };
    }
    let parsed;
    try {
      parsed = parsePhoneNumber(
        compact,
        activeCountryCode.value || props.countryHint || undefined
      );
    } catch {
      parsed = undefined;
    }
    if (parsed?.isValid()) {
      return {
        phone_country_code: `+${parsed.countryCallingCode}`,
        phone_number: parsed.nationalNumber,
      };
    }
    const m = matchCountryByDialPrefix(compact);
    if (!m) {
      return { phone_country_code: null, phone_number: null };
    }
    const rest = compact.slice(m.dial_code.length).replace(/\D/g, '');
    if (!rest) {
      return { phone_country_code: null, phone_number: null };
    }
    return { phone_country_code: m.dial_code, phone_number: rest };
  },
});
</script>

<template>
  <div>
    <div
      v-on-clickaway="() => closeCountryDropdown()"
      class="relative flex min-w-0 items-stretch h-8 min-h-8 transition-all duration-500 ease-in-out outline outline-1 outline-offset-[-1px] rounded-xl bg-n-alpha-black2"
      :class="[inputBorderClass, { 'cursor-not-allowed opacity-50': disabled }]"
    >
      <button
        type="button"
        class="flex h-8 min-h-8 shrink-0 items-center justify-center gap-2.5 rounded-l-xl border-0 bg-transparent px-3.5 min-w-[4.75rem] text-n-slate-12 outline-none hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 disabled:pointer-events-none disabled:opacity-50"
        :disabled="disabled"
        :aria-expanded="showDropdown"
        aria-haspopup="listbox"
        @click="toggleCountryDropdown"
      >
        <span
          v-if="activeCountry"
          class="min-w-[1.25rem] select-none text-center text-base leading-none"
          aria-hidden="true"
        >
          {{ activeCountry.emoji }}
        </span>
        <span
          v-else
          class="i-lucide-globe size-4 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
        <span
          class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
      </button>
      <Input
        v-model="rawPhoneInput"
        type="tel"
        :placeholder="placeholder"
        :disabled="disabled"
        custom-input-class="!rounded-none !rounded-r-xl !border-0 !bg-transparent !shadow-none !outline-none !outline-0 !h-8 !min-h-8 !max-h-8 focus:!outline-none"
        class="!flex-1 min-w-0 w-full !min-h-0 !flex-col"
        @focus="onPhoneFocus"
        @blur="onPhoneBlur"
      />
      <DropdownMenu
        v-if="showDropdown"
        :menu-items="countryList"
        show-search
        class="z-[100] min-w-72 w-72 max-w-[min(100vw-2rem,20rem)] mt-2 overflow-y-auto ltr:left-0 rtl:right-0 top-full max-h-60"
        @action="onSelectCountry"
      />
    </div>
    <template v-if="phoneNumberError">
      <p
        class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
      >
        {{ phoneNumberError }}
      </p>
    </template>
  </div>
</template>
